import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Link,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Smartphone as SmartphoneIcon,
  Keyboard as KeyboardIcon,
} from '@mui/icons-material';
import { useParams, Link as RouterLink } from 'react-router-dom';
import authService, {
  SetupTokenValidationResponse,
  StartSetupWithTokenResponse,
} from 'shared/services/authService';
import { usePageTitle, getPageTitle } from 'shared/hooks/usePageTitle';

type SetupStep = 'loading' | 'invalid' | 'scan' | 'verify' | 'complete';

const STEPS = ['Escanear QR', 'Verificar Código'];

export const SetupTwoFactorWithToken: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  // Page state
  const [currentStep, setCurrentStep] = useState<SetupStep>('loading');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Token validation data
  const [validationData, setValidationData] = useState<SetupTokenValidationResponse['data'] | null>(null);

  // Setup data
  const [setupData, setSetupData] = useState<StartSetupWithTokenResponse | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [copiedManualKey, setCopiedManualKey] = useState(false);

  // Verification
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  usePageTitle(
    getPageTitle('Configurar Autenticación 2FA'),
    'Configurar autenticación de dos factores'
  );

  // Validate token and start setup on mount
  useEffect(() => {

    const validateAndStartSetup = async () => {
      if (!token) {
        setCurrentStep('invalid');
        setError('Token no proporcionado');
        return;
      }

      try {
        // Step 1: Validate the token
        const validationResponse = await authService.validateSetupToken(token);

        if (!validationResponse.success || !validationResponse.valid || !validationResponse.data) {
          setCurrentStep('invalid');
          setError(validationResponse.error || 'Token inválido o expirado');
          return;
        }

        setValidationData(validationResponse.data);

        // Step 2: Start the setup process
        const setupResponse = await authService.startSetupWithToken(token);
        setSetupData(setupResponse);
        setCurrentStep('scan');
        setActiveStepIndex(0);
      } catch (err: unknown) {
        setCurrentStep('invalid');
        setError(err instanceof Error ? err.message : 'Error al validar el token');
      }
    };

    validateAndStartSetup();
  }, [token]);

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === 5 && newCode.every((d) => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeString?: string) => {
    if (!token) return;

    const finalCode = codeString || code.join('');
    if (finalCode.length !== 6) return;

    setIsVerifying(true);
    setError(null);

    try {
      const response = await authService.verifySetupWithToken(token, finalCode);

      if (response.success) {
        setCurrentStep('complete');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Código inválido');
      setCode(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopyManualKey = async () => {
    if (!setupData?.manualEntryKey) return;

    try {
      await navigator.clipboard.writeText(setupData.manualEntryKey);
      setCopiedManualKey(true);
      setTimeout(() => setCopiedManualKey(false), 2000);
    } catch (err: unknown) {
    }
  };

  const handleContinueToVerify = () => {
    setCurrentStep('verify');
    setActiveStepIndex(1);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  // Loading state
  if (currentStep === 'loading') {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Validando token de configuración...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Invalid token state
  if (currentStep === 'invalid') {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 3,
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 450 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <SecurityIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Enlace Inválido
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error || 'El enlace de configuración no es válido o ha expirado.'}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Por favor contacte a su administrador para obtener un nuevo enlace de configuración.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  bgcolor: (theme) => theme.palette.background.buttonBg,
                  color: (theme) => theme.palette.background.buttonText,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.background.buttonBg,
                    filter: 'brightness(0.9)',
                  },
                }}
              >
                Ir a Inicio de Sesión
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // Complete state
  if (currentStep === 'complete') {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 3,
          }}
        >
          <Card sx={{ width: '100%', maxWidth: 450 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckIcon sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                ¡Configuración Completada!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                La autenticación de dos factores ha sido configurada exitosamente para su cuenta.
              </Typography>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                Ya puede iniciar sesión con su contraseña y código de autenticador.
              </Alert>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  bgcolor: (theme) => theme.palette.background.buttonBg,
                  color: (theme) => theme.palette.background.buttonText,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.background.buttonBg,
                    filter: 'brightness(0.9)',
                  },
                }}
              >
                Ir a Inicio de Sesión
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 600 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: (theme) => theme.palette.background.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <SecurityIcon sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Typography variant="h5" align="center" fontWeight="bold">
                Configurar Autenticación 2FA
              </Typography>
              {validationData && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {validationData.firstName} {validationData.lastName} - {validationData.companyName}
                </Typography>
              )}
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ mb: 4 }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Step 1: Scan QR Code */}
            {currentStep === 'scan' && setupData && (
              <Box>
                <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                  Escanee el código QR con su aplicación de autenticación
                </Typography>

                {/* QR Code */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 3,
                    bgcolor: 'white',
                  }}
                >
                  <img
                    src={setupData.qrCodeUrl}
                    alt="QR Code for 2FA Setup"
                    style={{ width: 200, height: 200 }}
                  />
                </Paper>

                {/* Manual Entry Toggle */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setShowManualEntry(!showManualEntry)}
                    sx={{
                      cursor: 'pointer',
                      color: (theme) => theme.palette.background.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mx: 'auto',
                    }}
                  >
                    <KeyboardIcon fontSize="small" />
                    {showManualEntry ? 'Ocultar clave manual' : '¿No puede escanear? Ingrese la clave manualmente'}
                  </Link>
                </Box>

                {/* Manual Entry Key */}
                {showManualEntry && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Clave de configuración manual:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          flex: 1,
                          wordBreak: 'break-all',
                          fontWeight: 'bold',
                          letterSpacing: '0.1em',
                        }}
                      >
                        {setupData.manualEntryKey}
                      </Typography>
                      <Tooltip title={copiedManualKey ? '¡Copiado!' : 'Copiar clave'}>
                        <IconButton onClick={handleCopyManualKey} size="small">
                          {copiedManualKey ? <CheckIcon color="success" /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                )}

                {/* App suggestions */}
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <SmartphoneIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Aplicaciones recomendadas:
                  </Typography>
                  <Typography variant="body2">
                    Google Authenticator, Microsoft Authenticator, Authy, 1Password
                  </Typography>
                </Paper>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleContinueToVerify}
                  sx={{
                    py: 1.5,
                    bgcolor: (theme) => theme.palette.background.buttonBg,
                    color: (theme) => theme.palette.background.buttonText,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.background.buttonBg,
                      filter: 'brightness(0.9)',
                    },
                  }}
                >
                  Continuar
                </Button>
              </Box>
            )}

            {/* Step 2: Verify Code */}
            {currentStep === 'verify' && (
              <Box>
                <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                  Ingrese el código de 6 dígitos de su aplicación
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                  El código cambia cada 30 segundos
                </Typography>

                {/* Code Input */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 3,
                  }}
                  onPaste={handlePaste}
                >
                  {code.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      disabled={isVerifying}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: (theme) => theme.palette.background.primary,
                          },
                        },
                      }}
                      slotProps={{
                        input: {
                          sx: {
                            width: 48,
                            height: 56,
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                          },
                        },
                        htmlInput: {
                          maxLength: 1,
                          inputMode: 'numeric',
                          pattern: '[0-9]',
                          'aria-label': `Dígito ${index + 1} de 6`,
                          style: { textAlign: 'center' },
                        },
                      }}
                    />
                  ))}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleVerify()}
                  disabled={code.some((d) => d === '') || isVerifying}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    bgcolor: (theme) => theme.palette.background.buttonBg,
                    color: (theme) => theme.palette.background.buttonText,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.background.buttonBg,
                      filter: 'brightness(0.9)',
                    },
                  }}
                >
                  {isVerifying ? <CircularProgress size={24} color="inherit" /> : 'Verificar'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setCurrentStep('scan');
                      setActiveStepIndex(0);
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: (theme) => theme.palette.background.primary,
                    }}
                  >
                    Volver al paso anterior
                  </Link>
                </Box>
              </Box>
            )}

          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default SetupTwoFactorWithToken;
